"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowUpRight, Code2, Copy, Check, ExternalLink, Image as ImageIcon, MessageSquare, PanelTop, BarChart3, PenLine, ShieldCheck, Sparkles, Play } from "lucide-react";
import Image from "next/image";
import { useState, useCallback } from "react";
import { Badge } from "@/lib/components/badge";
import { Button } from "@/lib/components/button";
import { Card } from "@/lib/components/card";
import { useI18n } from "@llmgateway/i18n/react";
interface Template {
  nameKey: string;
  descriptionKey: string;
  templateId: string;
  demoUrl: string;
  demoLabelKey?: string;
  image: string;
  icon: typeof ImageIcon;
  tags: string[];
  gradient: string;
  featured?: boolean;
}
const templates: Template[] = [{
  nameKey: "public.templates.items.imageGeneration.name",
  descriptionKey: "public.templates.items.imageGeneration.description",
  templateId: "image-generation",
  demoUrl: "https://llm-image-generation.vercel.app",
  image: "/templates/image-gen.png",
  icon: ImageIcon,
  tags: ["TypeScript", "Next.js", "AI SDK"],
  gradient: "from-violet-500/20 via-fuchsia-500/20 to-pink-500/20",
  featured: true
}, {
  nameKey: "public.templates.items.chatbot.name",
  descriptionKey: "public.templates.items.chatbot.description",
  templateId: "ai-chatbot",
  demoUrl: "https://llm-ai-chatbot-brown.vercel.app",
  image: "/templates/chatbot.png",
  icon: MessageSquare,
  tags: ["TypeScript", "Next.js", "AI SDK"],
  gradient: "from-sky-500/20 via-blue-500/20 to-indigo-500/20",
  featured: true
}, {
  nameKey: "public.templates.items.ogImage.name",
  descriptionKey: "public.templates.items.ogImage.description",
  templateId: "og-image-generator",
  demoUrl: "https://llm-og-image-generator.vercel.app",
  image: "/templates/og-image.png",
  icon: PanelTop,
  tags: ["TypeScript", "Next.js", "AI SDK"],
  gradient: "from-orange-500/20 via-amber-500/20 to-yellow-500/20"
}, {
  nameKey: "public.templates.items.feedback.name",
  descriptionKey: "public.templates.items.feedback.description",
  templateId: "feedback-dashboard",
  demoUrl: "https://llm-feedback-dashboard.vercel.app",
  image: "/templates/feedback.png",
  icon: BarChart3,
  tags: ["TypeScript", "Next.js", "AI SDK"],
  gradient: "from-emerald-500/20 via-green-500/20 to-teal-500/20"
}, {
  nameKey: "public.templates.items.writing.name",
  descriptionKey: "public.templates.items.writing.description",
  templateId: "writing-assistant",
  demoUrl: "https://llm-writing-assistant.vercel.app",
  image: "/templates/writing-assistant.png",
  icon: PenLine,
  tags: ["TypeScript", "Next.js", "AI SDK"],
  gradient: "from-rose-500/20 via-pink-500/20 to-fuchsia-500/20"
}, {
  nameKey: "public.templates.items.qa.name",
  descriptionKey: "public.templates.items.qa.description",
  templateId: "qa-agent",
  demoUrl: "https://youtu.be/-ai9eVvXvZE",
  demoLabelKey: "public.templates.watchDemo",
  image: "/templates/qa-agent.png",
  icon: ShieldCheck,
  tags: ["TypeScript", "Next.js", "AI SDK"],
  gradient: "from-cyan-500/20 via-teal-500/20 to-blue-500/20"
}];
export function TemplateCards() {
  const $ = _c(24);
  const {
    t
  } = useI18n();
  const [copiedTemplateId, setCopiedTemplateId] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = templateId => {
      navigator.clipboard.writeText(`npx @llmgateway/cli init --template ${templateId}`);
      setCopiedTemplateId(templateId);
      setTimeout(() => setCopiedTemplateId(null), 2000);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const copyToClipboard = t0;
  if (templates.length === 0) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Sparkles className="mx-auto h-12 w-12 text-muted-foreground/50" />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t) {
      t2 = t("public.templates.empty");
      $[2] = t;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t2) {
      t3 = <div className="text-center py-16">{t1}<p className="mt-4 text-muted-foreground">{t2}</p></div>;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t1;
  if ($[6] !== copiedTemplateId || $[7] !== t) {
    t1 = templates.map(template => {
      const name = t(template.nameKey);
      return <Card key={template.nameKey} className="group relative flex flex-col overflow-hidden border-0 bg-gradient-to-br from-background to-muted/30 shadow-xl hover:shadow-2xl transition-all duration-500"><div className={`absolute inset-0 bg-gradient-to-br ${template.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />{template.featured && <div className="absolute top-4 right-4 z-10"><Badge className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white border-0 shadow-lg"><Sparkles className="mr-1 h-3 w-3" />{t("public.common.featured")}</Badge></div>}<div className="relative aspect-video overflow-hidden"><Image src={template.image} alt={t("public.templates.previewAlt", {
            name
          })} width={1200} height={675} className="h-full w-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" /></div><div className="relative flex flex-1 flex-col p-6 space-y-6"><div className="space-y-3"><div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-lg"><template.icon className="h-5 w-5 text-white" /></div><h3 className="text-2xl font-bold tracking-tight">{name}</h3></div><p className="text-muted-foreground leading-relaxed">{t(template.descriptionKey)}</p></div><div className="flex flex-wrap gap-2">{template.tags.map(_temp)}</div><div className="flex flex-col sm:flex-row gap-3 pt-2 mt-auto"><Button asChild={true} className="flex-1 gap-2 font-semibold"><a href={template.demoUrl} target="_blank" rel="noopener noreferrer">{template.demoLabelKey ? <Play className="h-4 w-4" /> : <ExternalLink className="h-4 w-4" />}{template.demoLabelKey ? t(template.demoLabelKey) : t("public.common.liveDemo")}<ArrowUpRight className="h-4 w-4 opacity-50 group-hover:opacity-100 transition-opacity" /></a></Button><Button variant="outline" className="gap-2" onClick={() => copyToClipboard(template.templateId)}>{copiedTemplateId === template.templateId ? <><Check className="h-4 w-4 text-green-500" />{t("public.common.copied")}</> : <><Copy className="h-4 w-4" />{t("public.common.clone")}</>}</Button></div></div><div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500" /></Card>;
    });
    $[6] = copiedTemplateId;
    $[7] = t;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted"><Sparkles className="h-8 w-8 text-muted-foreground" /></div>;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t) {
    t3 = t("public.templates.moreTitle");
    $[10] = t;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t3) {
    t4 = <h3 className="text-xl font-semibold text-muted-foreground">{t3}</h3>;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t) {
    t5 = t("public.templates.moreDescription");
    $[14] = t;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t5) {
    t6 = <p className="text-sm text-muted-foreground/80">{t5}</p>;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t6) {
    t7 = <Card className="relative overflow-hidden border-dashed border-2 bg-muted/20 hover:bg-muted/30 transition-colors duration-300"><div className="p-6 space-y-6 flex flex-col items-center justify-center min-h-[320px] text-center">{t2}<div className="space-y-2">{t4}{t6}</div></div></Card>;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t1 || $[22] !== t7) {
    t8 = <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-2 max-w-5xl mx-auto">{t1}{t7}</div>;
    $[21] = t1;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}
function _temp(tag) {
  return <Badge key={tag} variant="secondary" className="bg-muted/80 hover:bg-muted transition-colors"><Code2 className="mr-1 h-3 w-3" />{tag}</Badge>;
}